import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { AllCostSearch } from '../types/billing.types';
import { toAllCostFilterSearch, toAllCostFilterValues } from '../utils/billingFilters';
import { getQueueDefaultPeriod } from '../utils/period';

const route = getRouteApi('/_auth/billing/all-cost');

const normalizeFilterValues = (values: Record<string, string>) =>
  toAllCostFilterValues(toAllCostFilterSearch(values));

export function useAllCostFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toAllCostFilterValues(search), [search]);
  const defaultValues = useMemo(() => getQueueDefaultPeriod(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toAllCostFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): AllCostSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search, defaultValues],
  );

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, params };
}
